/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';

import { SplashScreen } from 'src/components/loading-screen';
import { useSnackbar } from 'src/components/snackbar';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { LoginRepository } from 'src/repositories/login/login.repository';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useUserStore } from 'src/stores/user/user.store';
import { jwtDecode } from 'src/utils/isValidToken';

import { useAuthStore } from '../auth.store';

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const { isLoading } = useAuthStore();

  return <>{isLoading ? <SplashScreen /> : <Container>{children}</Container>}</>;
}

function Container({ children }: Props) {
  const api = useAxiosClientContext();
  const { toastWarning } = useSnackbar();
  const [checked, setChecked] = useState(false);
  const router = useRouter();
  const { setUser } = useUserStore();
  const { isAuthenticated, accessToken, refreshToken, clearTokens: clearSession, setIsAuthenticated } = useAuthStore();

  const initializeAuth = async () => {
    const repository = new LoginRepository(api);
    if (accessToken && refreshToken) {
      const decoded = jwtDecode(accessToken);
      const userId = decoded.sub;
      const response = await repository.getUserData(userId);
      if (response.isLeft()) {
        clearSession();
        router.replace(paths.auth.login);
        setIsAuthenticated(false);
        console.error(response.value);
        toastWarning('Erro ao obter dados do usuário. Por favor, faça login novamente.');
      } else {
        setUser(response.value);
        setIsAuthenticated(true);
      }
    } else {
      clearSession();
      router.replace(paths.auth.login);
      setIsAuthenticated(false);
    }
  };

  const check = useCallback(() => {
    if (!isAuthenticated) {
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();
      const href = `${paths.auth.login}?${searchParams}`;
      router.replace(href);
    } else {
      setChecked(true);
    }
  }, [isAuthenticated, router]);

  const auth = async () => {
    await initializeAuth();
    check();
  };

  useEffect(() => {
    auth();
  }, [isAuthenticated, router]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
